import React, { useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useShowStore } from '../../stores/showStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { vmixAPI } from '../../lib/api';
import RundownTable from '../rundown/RundownTable';
import CueTypeBadge from '../rundown/CueTypeBadge';

function VmixPanel({ showId }) {
  const vmixConnected = useConnectionStore(s => s.vmixConnected);
  const vmixInputs = useConnectionStore(s => s.vmixInputs, shallow);
  const vmixTally = useConnectionStore(s => s.vmixTally, shallow);
  const [connecting, setConnecting] = useState(false);

  async function handleConnect() {
    setConnecting(true);
    try {
      await vmixAPI.connect(showId);
    } catch (err) {
      console.error('vMix connect failed:', err.message);
    } finally {
      setConnecting(false);
    }
  }

  async function handleRefreshInputs() {
    try {
      await vmixAPI.inputs(showId);
    } catch (err) {
      console.error('Refresh inputs failed:', err.message);
    }
  }

  async function handleFunction(fn, input) {
    try {
      await vmixAPI.function(showId, fn, input);
    } catch (err) {
      console.error('vMix function failed:', err.message);
    }
  }

  return (
    <div className="bg-app-well border border-app-line rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">vMix</h3>
        <div className={`flex items-center gap-1.5 text-xs ${vmixConnected ? 'text-green-400' : 'text-gray-600'}`}>
          <div className={`w-1.5 h-1.5 rounded-full ${vmixConnected ? 'bg-green-500' : 'bg-gray-700'}`} />
          {vmixConnected ? 'Connected' : 'Disconnected'}
        </div>
      </div>

      {!vmixConnected ? (
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="w-full py-2 text-xs bg-app-surface hover:bg-app-border text-gray-300 rounded border border-app-border-strong transition-colors"
        >
          {connecting ? 'Connecting...' : 'Connect vMix'}
        </button>
      ) : (
        <>
          {/* Tally display */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-red-900/20 border border-red-800 rounded p-2 text-center">
              <div className="text-xs text-red-500 uppercase tracking-wide mb-1">Program</div>
              <div className="text-sm font-mono font-medium text-red-400">
                {vmixTally.program?.join(', ') || '—'}
              </div>
            </div>
            <div className="bg-green-900/20 border border-green-800 rounded p-2 text-center">
              <div className="text-xs text-green-500 uppercase tracking-wide mb-1">Preview</div>
              <div className="text-sm font-mono font-medium text-green-400">
                {vmixTally.preview?.join(', ') || '—'}
              </div>
            </div>
          </div>

          {/* Quick functions */}
          <div className="grid grid-cols-3 gap-1.5 mb-3">
            {['Cut', 'Fade', 'Zoom', 'Wipe', 'Slide', 'FlyRotate'].map(fn => (
              <button
                key={fn}
                onClick={() => handleFunction(fn)}
                className="py-1.5 text-xs bg-app-surface hover:bg-app-border text-gray-400 rounded border border-app-border transition-colors"
              >
                {fn}
              </button>
            ))}
          </div>

          {/* Input List */}
          {vmixInputs.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600">Inputs ({vmixInputs.length})</span>
                <button
                  onClick={handleRefreshInputs}
                  className="text-xs text-gray-700 hover:text-gray-400 transition-colors"
                >
                  Refresh
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {vmixInputs.map(input => (
                  <div
                    key={input.key || input.number}
                    className={`flex items-center justify-between px-2 py-1 rounded text-xs border ${
                      vmixTally.program?.includes(input.number)
                        ? 'bg-red-900/20 border-red-800 text-red-400'
                        : vmixTally.preview?.includes(input.number)
                          ? 'bg-green-900/20 border-green-800 text-green-400'
                          : 'bg-app-well border-app-line text-gray-500'
                    }`}
                  >
                    <span>{input.number}. {input.title || input.type}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleFunction('PreviewInput', input.number)}
                        className="px-1.5 py-0.5 text-xs bg-green-900/30 text-green-500 rounded hover:bg-green-900/50 transition-colors"
                      >
                        PRV
                      </button>
                      <button
                        onClick={() => handleFunction('Cut', input.number)}
                        className="px-1.5 py-0.5 text-xs bg-red-900/30 text-red-500 rounded hover:bg-red-900/50 transition-colors"
                      >
                        CUT
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NextCuePanel() {
  const onAirCue = useShowStore(s => s.cues.find(c => c.status === 'on_air') || null);
  const nextCue = useShowStore(s => {
    const idx = s.cues.findIndex(c => c.status === 'on_air');
    return s.cues.slice(idx + 1).find(c => c.status === 'pending') || s.cues.find(c => c.status === 'pending') || null;
  });

  return (
    <div className="bg-app-well border border-app-line rounded-lg p-4 space-y-3">
      {/* On Air */}
      <div>
        <div className="text-xs text-gray-600 uppercase tracking-wide mb-1.5">On Air</div>
        {onAirCue ? (
          <div className="bg-red-900/20 border border-red-800 rounded p-2">
            <div className="flex items-center gap-2 mb-1">
              <CueTypeBadge type={onAirCue.cue_type} compact />
              <span className="text-sm font-medium text-app-fg">{onAirCue.label}</span>
            </div>
            {onAirCue.vmix_actions?.length > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                {onAirCue.vmix_actions.length} vMix action{onAirCue.vmix_actions.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-700 italic">No cue on air</div>
        )}
      </div>

      {/* Next */}
      <div>
        <div className="text-xs text-gray-600 uppercase tracking-wide mb-1.5">Next</div>
        {nextCue ? (
          <div className="bg-amber-900/10 border border-amber-800/40 rounded p-2">
            <div className="flex items-center gap-2 mb-1">
              <CueTypeBadge type={nextCue.cue_type} compact />
              <span className="text-sm font-medium text-amber-300">{nextCue.label}</span>
            </div>
            {nextCue.vmix_actions && nextCue.vmix_actions.length > 0 && (
              <div className="mt-2 space-y-1">
                <div className="text-xs text-gray-600">vMix Actions:</div>
                {nextCue.vmix_actions.map((action, i) => (
                  <div key={i} className="text-xs font-mono text-gray-500 bg-app-header px-2 py-1 rounded">
                    {action.function}
                    {action.input ? ` input:${action.input}` : ''}
                    {action.delay_ms ? ` +${action.delay_ms}ms` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-700 italic">No next cue</div>
        )}
      </div>
    </div>
  );
}

export default function TDView({ showId, show }) {
  return (
    <div className="flex h-full">
      {/* Left: Rundown */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <RundownTable showId={showId} readOnly={true} />
      </div>

      {/* Right: TD Panels */}
      <div className="w-72 flex-shrink-0 border-l border-app-line-hard overflow-y-auto bg-app-canvas p-3 space-y-3">
        <NextCuePanel />
        <VmixPanel showId={showId} />
      </div>
    </div>
  );
}
